import React, { useEffect, useState } from 'react';

import { Close } from '@mui/icons-material';
import {
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import ContentCard from '../../../components/ContentCard';
import EditField from '../../../components/EditField';
import Link from '../../../components/Link';
import SearchField from '../../../components/SearchField';
import {
  useGetExamsQuery,
  useUpdateExamMarkingRequestMutation,
} from '../../../services/exams';
import { SUBJECTS } from '../../../utils/common/constants';
import useError from '../../../utils/react/hooks/useError';

type Exam = {
  subject: string,
  examId: string,
  label: string,
};

interface RequestExamCardProps {
  requestId: string,
  exam?: Exam
}

const RequestExamCard = ({
  requestId, exam,
}: RequestExamCardProps) => {
  const [selectedSubjectLabel, setSelectedSubjectLabel] = useState<string | undefined>('');
  const [subject, setSubject] = useState<string | undefined>('');
  const [searchStr, setSearchStr] = useState('');

  const { data: searchedExams, isFetching: isSearchedExamsLoading } = useGetExamsQuery({
    subject: subject || '',
  }, { skip: searchStr === '' || !subject });

  useEffect(() => {
    if (exam) {
      setSelectedSubjectLabel(SUBJECTS[exam.subject].label);
    }
  }, [exam]);

  const [updateMarkingRequest, {
    isError: isUpdateMarkingRequestError,
    error: updateMarkingRequestError,
  }] = useUpdateExamMarkingRequestMutation();

  useError(isUpdateMarkingRequestError, updateMarkingRequestError);

  return (
    <ContentCard title="Exam" variant="outlined">
      <Stack direction="column" spacing={2}>
        {exam ? (
          <ContentCard variant="contained" padding="small">
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              paddingLeft={2}
              paddingRight={2}
            >
              <Link href={`/admin/exams/${exam.subject}/${exam.examId}`} fontWeight="bold">
                {selectedSubjectLabel} {exam.label}
              </Link>
              <IconButton onClick={() => {
                updateMarkingRequest({
                  requestId,
                  newAttributes: {
                    exam: '',
                  },
                });
              }}
              >
                <Close />
              </IconButton>
            </Stack>
          </ContentCard>
        ) : (
          <Stack direction="column" spacing={1}>
            <EditField
              placeholder="Select a subject..."
              value={subject}
              type="select"
              typeOptions={Object.values(SUBJECTS)
                .map((s) => ({
                  value: s.key,
                  label: s.label,
                }))}
              onSubmit={(s) => setSubject(s as string)}
            />
            {subject && (
              <SearchField
                type="dropdown"
                placeholder="Search exams..."
                options={searchedExams?.data.filter((e) => {
                  const examSearchStr = e.label.trim().toLowerCase();
                  const containsSearch = examSearchStr.includes(searchStr.trim().toLowerCase());

                  return e.subject === subject && containsSearch;
                }) || []}
                getOptionKey={(e) => e.id}
                getOptionLabel={(e) => e.label}
                onSearch={(search) => setSearchStr(search)}
                loading={isSearchedExamsLoading}
                onSelect={(e) => {
                  updateMarkingRequest({
                    requestId,
                    newAttributes: {
                      exam: `${e.subject}#${e.examId}`,
                    },
                  });
                }}
                renderOption={(props, e) => (
                  <li {...props}>
                    <Typography key={e.id}>
                      {e.label}
                    </Typography>
                  </li>
                )}
                clearOnSelect
              />
            )}
          </Stack>
        )}
      </Stack>
    </ContentCard>
  );
};

export default RequestExamCard;
